import type {
  AlertRecord,
  ImpersonationState,
  LocationSummary,
} from "@restlab/shared";
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  FileText,
  CloudOff,
  ContactRound,
  LayoutGrid,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareText,
  PhoneCall,
  PauseCircle,
  RefreshCw,
  CircleUserRound,
  Settings2,
  ShieldCheck,
  UsersRound,
  Utensils,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { currentClientRelease, type ReleaseNoticeStatus } from "../release";
import { Logo } from "./Logo";

const defaultLocation = "0198f000-0000-7000-8000-000000000101";

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeGroupAlerts, setActiveGroupAlerts] = useState(0);
  const [overlapNotice, setOverlapNotice] = useState<AlertRecord | null>(null);
  const [releaseNotice, setReleaseNotice] =
    useState<ReleaseNoticeStatus | null>(null);
  const knownAlertIds = useRef<Set<string> | null>(null);
  const isGroupRoute = [
    "/group",
    "/migration",
    "/billing",
    "/operations/alerts",
  ].some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  const [groupMenuOpen, setGroupMenuOpen] = useState(isGroupRoute);
  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    active: false,
  });
  const [impersonationLoaded, setImpersonationLoaded] = useState(false);
  const activeLocationId =
    params.locationId ??
    localStorage.getItem("restlab-location") ??
    defaultLocation;
  const isVisitBook = /^\/locations\/[^/]+\/book/.test(location.pathname);
  const effectiveAccountType = impersonation.active
    ? impersonation.accountType
    : user?.accountType;
  const accountContextLoaded =
    user?.role !== "super_admin" || impersonationLoaded;
  const hasGroupAccess =
    accountContextLoaded &&
    effectiveAccountType !== "standalone" &&
    ["super_admin", "group_admin"].includes(user?.role ?? "");
  const canManageUsers =
    hasGroupAccess && ["super_admin", "group_admin"].includes(user?.role ?? "");

  useEffect(() => {
    if (!user || (user.role === "super_admin" && !impersonation.active)) {
      setReleaseNotice(null);
      return;
    }
    let active = true;
    const clientRelease = currentClientRelease();
    const loadReleaseNotice = async () => {
      try {
        const notice = await api<ReleaseNoticeStatus>(
          `/api/v1/release-notice?clientRelease=${encodeURIComponent(clientRelease)}`,
        );
        if (!active) return;
        const noticeId = `${notice.currentRelease}:${notice.publishedAt ?? "unpublished"}`;
        const dismissedNotice = sessionStorage.getItem(
          "restlab-dismissed-release-notice",
        );
        setReleaseNotice(
          notice.updateAvailable && dismissedNotice !== noticeId
            ? notice
            : null,
        );
      } catch {
        // El control de versión nunca debe interrumpir el servicio del restaurante.
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void loadReleaseNotice();
    };
    void loadReleaseNotice();
    const interval = window.setInterval(() => void loadReleaseNotice(), 30_000);
    window.addEventListener("focus", loadReleaseNotice);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", loadReleaseNotice);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [impersonation.active, user?.id, user?.role]);

  useEffect(() => {
    if (!hasGroupAccess) {
      setActiveGroupAlerts(0);
      knownAlertIds.current = null;
      return;
    }
    let active = true;
    const loadAlerts = async () => {
      try {
        const alerts = await api<AlertRecord[]>("/api/v1/alerts");
        if (!active) return;
        const unresolved = alerts.filter((alert) => alert.state !== "resolved");
        setActiveGroupAlerts(unresolved.length);
        if (knownAlertIds.current) {
          const freshOverlap = unresolved.find(
            (alert) =>
              alert.category === "booking_overlap" &&
              !knownAlertIds.current?.has(alert.id),
          );
          if (freshOverlap) setOverlapNotice(freshOverlap);
        }
        knownAlertIds.current = new Set(alerts.map((alert) => alert.id));
      } catch {
        // Una actualización puntual no debe interrumpir la navegación.
      }
    };
    void loadAlerts();
    const interval = window.setInterval(() => void loadAlerts(), 15_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [hasGroupAccess]);

  useEffect(() => {
    const loadLocations = () => {
      void api<LocationSummary[]>("/api/v1/locations").then(setLocations);
    };
    loadLocations();
    window.addEventListener("restlab:locations-changed", loadLocations);
    return () =>
      window.removeEventListener("restlab:locations-changed", loadLocations);
  }, []);
  useEffect(() => {
    if (user?.role !== "super_admin") {
      setImpersonationLoaded(true);
      return;
    }
    setImpersonationLoaded(false);
    void api<ImpersonationState>("/api/v1/admin/impersonation").then(
      (state) => {
        setImpersonation(state);
        setImpersonationLoaded(true);
      },
      () => setImpersonationLoaded(true),
    );
  }, [user?.role]);
  useEffect(() => {
    if ("serviceWorker" in navigator && import.meta.env.PROD)
      void navigator.serviceWorker.register("/sw.js");
  }, []);
  useEffect(() => {
    if (params.locationId)
      localStorage.setItem("restlab-location", params.locationId);
  }, [params.locationId]);
  useEffect(() => {
    if (isGroupRoute) setGroupMenuOpen(true);
  }, [isGroupRoute]);

  const current =
    locations.find((item) => item.id === activeLocationId) ?? locations[0];
  const effectiveLocationId = current?.id ?? activeLocationId;
  const isStandby = current?.operationalMode === "standby";
  const billingTarget =
    effectiveAccountType === "standalone"
      ? `/locations/${effectiveLocationId}/billing`
      : "/billing";
  const canManageBilling =
    ["super_admin", "group_admin"].includes(user?.role ?? "") ||
    (user?.role === "manager" && effectiveAccountType === "standalone");
  const canManageLocation = ["super_admin", "group_admin", "manager"].includes(
    user?.role ?? "",
  );
  const isRestaurantRoute = /^\/locations\/[^/]+\//.test(location.pathname);
  const isStandbySafeRoute =
    location.pathname.includes("/settings") ||
    location.pathname.includes("/billing") ||
    location.pathname.includes("/onboarding");
  const fullLocationNav = [
    {
      to: `/locations/${effectiveLocationId}/dashboard`,
      label: "Dashboard",
      icon: LayoutDashboard,
    },
    {
      to: `/locations/${effectiveLocationId}/book`,
      label: "Libro de visitas",
      icon: Utensils,
    },
    {
      to: `/locations/${effectiveLocationId}/customers`,
      label: "Clientes",
      icon: ContactRound,
    },
    {
      to: `/locations/${effectiveLocationId}/calendar`,
      label: "Calendario",
      icon: CalendarDays,
    },
    {
      to: `/locations/${effectiveLocationId}/floor-plan`,
      label: "Plano de salón",
      icon: LayoutGrid,
    },
    {
      to: `/locations/${effectiveLocationId}/reviews`,
      label: "Opiniones",
      icon: MessageSquareText,
    },
    {
      to: `/locations/${effectiveLocationId}/voice`,
      label: "Voice",
      icon: PhoneCall,
    },
    {
      to: `/locations/${effectiveLocationId}/statistics`,
      label: "Estadísticas",
      icon: BarChart3,
    },
    ...(canManageLocation
      ? [
          {
            to: `/locations/${effectiveLocationId}/team`,
            label: "Equipo",
            icon: CircleUserRound,
          },
        ]
      : []),
    ...(canManageBilling && effectiveAccountType === "standalone"
      ? [
          {
            to: `/locations/${effectiveLocationId}/billing`,
            label: "Facturación",
            icon: FileText,
          },
        ]
      : []),
    {
      to: `/locations/${effectiveLocationId}/settings`,
      label: "Configuración",
      icon: Settings2,
    },
  ];
  const permittedLocationNav = canManageLocation
    ? fullLocationNav
    : fullLocationNav.filter(
        (item) =>
          !["Plano de salón", "Voice", "Facturación", "Configuración"].includes(
            item.label,
          ),
      );
  const locationNav = isStandby
    ? permittedLocationNav.filter(
        (item) => item.to.endsWith("/settings") || item.to.endsWith("/billing"),
      )
    : permittedLocationNav;
  const groupNav = [
    { to: "/group", label: "Control de grupo", icon: BarChart3, end: true },
    ...(canManageUsers
      ? [
          {
            to: "/group/users",
            label: "Usuarios y accesos",
            icon: UsersRound,
            end: false,
          },
        ]
      : []),
    {
      to: "/group/standards",
      label: "Estándares",
      icon: ClipboardCheck,
      end: false,
    },
    { to: "/billing", label: "Facturación", icon: FileText, end: false },
    {
      to: "/operations/alerts",
      label: "Alertas",
      icon: AlertTriangle,
      end: false,
      badge: activeGroupAlerts,
    },
  ];

  function switchLocation(nextId: string) {
    localStorage.setItem("restlab-location", nextId);
    if (location.pathname.includes("/dashboard"))
      navigate(`/locations/${nextId}/dashboard`);
    else if (location.pathname.includes("/statistics"))
      navigate(`/locations/${nextId}/statistics`);
    else if (location.pathname.includes("/calendar"))
      navigate(`/locations/${nextId}/calendar`);
    else if (location.pathname.includes("/waitlist"))
      navigate(`/locations/${nextId}/waitlist`);
    else if (location.pathname.includes("/floor-plan"))
      navigate(`/locations/${nextId}/floor-plan`);
    else if (location.pathname.includes("/customers"))
      navigate(`/locations/${nextId}/customers`);
    else if (location.pathname.includes("/reviews"))
      navigate(`/locations/${nextId}/reviews`);
    else if (location.pathname.includes("/voice"))
      navigate(`/locations/${nextId}/voice`);
    else if (location.pathname.includes("/settings"))
      navigate(`/locations/${nextId}/settings`);
    else if (location.pathname.includes("/billing"))
      navigate(`/locations/${nextId}/billing`);
    else navigate(`/locations/${nextId}/dashboard`);
  }

  async function exitImpersonation() {
    await api("/api/v1/admin/impersonation/exit", { method: "POST" });
    window.location.assign("/admin");
  }

  return (
    <div className={`app-shell ${isVisitBook ? "app-shell--visit-book" : ""}`}>
      <a className="skip-link" href="#main-content">
        Saltar al contenido
      </a>
      <aside className={`sidebar ${mobileMenu ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <Logo onDark />
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileMenu(false)}
            aria-label="Cerrar menú"
          >
            <X />
          </button>
        </div>
        <nav aria-label="Navegación principal">
          {hasGroupAccess && (
            <>
              <button
                type="button"
                className={`nav-label nav-section-toggle ${groupMenuOpen ? "nav-section-toggle--open" : ""}`}
                onClick={() => setGroupMenuOpen((open) => !open)}
                aria-expanded={groupMenuOpen}
                aria-controls="group-navigation"
                aria-label={`${groupMenuOpen ? "Ocultar" : "Mostrar"} menú de grupo`}
              >
                <span>Grupo</span>
                {activeGroupAlerts > 0 && (
                  <span
                    className="nav-alert-badge"
                    aria-label={`${activeGroupAlerts} alertas activas`}
                  >
                    {activeGroupAlerts > 99 ? "99+" : activeGroupAlerts}
                  </span>
                )}
                <ChevronDown aria-hidden="true" />
              </button>
              {groupMenuOpen && (
                <div
                  id="group-navigation"
                  className="nav-section nav-section--open"
                >
                  {groupNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMobileMenu(false)}
                      className={({ isActive }) =>
                        isActive ? "nav-item nav-item--active" : "nav-item"
                      }
                    >
                      <item.icon />
                      {item.label}
                      {"badge" in item && item.badge > 0 && (
                        <span className="nav-alert-badge nav-alert-badge--item">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          )}
          <p
            className={`nav-label ${hasGroupAccess ? "nav-label--spaced" : ""}`}
          >
            Restaurante
          </p>
          {locationNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileMenu(false)}
              className={({ isActive }) =>
                isActive ? "nav-item nav-item--active" : "nav-item"
              }
            >
              <item.icon />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__bottom">
          <div className="system-health">
            <span className="health-dot" />
            Sistema operativo
          </div>
          <NavLink
            className="nav-item"
            to="/account"
            onClick={() => setMobileMenu(false)}
          >
            <CircleUserRound /> Cuenta y privacidad
          </NavLink>
          <button className="nav-item nav-button" onClick={() => void logout()}>
            <LogOut />
            Cerrar sesión
          </button>
        </div>
      </aside>
      {mobileMenu && (
        <button
          className="sidebar-backdrop"
          onClick={() => setMobileMenu(false)}
          aria-label="Cerrar navegación"
        />
      )}

      <div className="app-area">
        {user?.role === "super_admin" && (
          <div className="impersonation-banner">
            <ShieldCheck />
            <span>
              {impersonation.active ? (
                <>
                  Impersonando{" "}
                  <strong>
                    {impersonation.locationName ??
                      impersonation.organizationName}
                  </strong>
                </>
              ) : (
                "Acceso como súper admin"
              )}
            </span>
            <button onClick={() => void exitImpersonation()}>
              Volver a súper administración
            </button>
          </div>
        )}
        <header className="topbar">
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileMenu(true)}
            aria-label="Abrir menú"
          >
            <Menu />
          </button>
          <div className="context-selector">
            <label htmlFor="location-selector">Restaurante activo</label>
            <div className="select-wrap">
              <select
                id="location-selector"
                value={current?.id ?? activeLocationId}
                onChange={(event) => switchLocation(event.target.value)}
              >
                {locations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <ChevronDown aria-hidden="true" />
            </div>
            {isStandby && (
              <span className="standby-context-badge">
                <PauseCircle /> Standby
              </span>
            )}
          </div>
          <div className="topbar__actions">
            <div className="sync-state" title="Última sincronización: ahora">
              <CloudOff size={16} className="online-icon" />
              <span className="desktop-only">Sincronizado ahora</span>
            </div>
            <button
              className="icon-button desktop-only"
              aria-label="Cuenta y privacidad"
              onClick={() => navigate("/account")}
            >
              <Settings2 />
            </button>
            <div className="user-menu">
              <span className="avatar">
                {user?.name
                  .split(" ")
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span className="desktop-only">
                <strong>{user?.name}</strong>
                <small>
                  {user?.role === "super_admin"
                    ? "Súper admin"
                    : user?.role === "group_admin"
                      ? "Administrador"
                      : user?.role === "manager"
                        ? "Manager"
                        : "Recepción"}
                </small>
              </span>
            </div>
          </div>
        </header>
        <main id="main-content" className="main-content" tabIndex={-1}>
          {isVisitBook && (
            <button
              className="visit-menu-trigger"
              onClick={() => setMobileMenu(true)}
              aria-label="Abrir navegación"
            >
              <Menu />
              <span>Menú</span>
            </button>
          )}
          {isStandby && isRestaurantRoute && !isStandbySafeRoute ? (
            <section className="standby-lock-panel panel">
              <span className="standby-lock-panel__icon">
                <PauseCircle />
              </span>
              <p className="eyebrow">Modo estacional</p>
              <h1>{current?.name} está en Standby</h1>
              <p>
                Toda la información, reservas, clientes, planos y configuración
                permanece guardada. La operativa diaria y la página pública de
                reservas están desactivadas mientras este plan esté activo.
              </p>
              <div className="standby-lock-panel__actions">
                {canManageBilling && (
                  <NavLink
                    className="button button--primary"
                    to={billingTarget}
                  >
                    Reactivar suscripción
                  </NavLink>
                )}
                <NavLink
                  className="button button--outline"
                  to={`/locations/${effectiveLocationId}/settings`}
                >
                  Ver configuración
                </NavLink>
              </div>
              {!canManageBilling && (
                <small>
                  Solicita a la persona responsable de facturación del grupo que
                  reactive un plan operativo.
                </small>
              )}
            </section>
          ) : (
            children
          )}
        </main>
        {overlapNotice && (
          <aside
            className="group-overlap-toast"
            role="status"
            aria-live="polite"
          >
            <span className="group-overlap-toast__icon">
              <AlertTriangle />
            </span>
            <div>
              <strong>{overlapNotice.title}</strong>
              <p>{overlapNotice.detail}</p>
              <button
                type="button"
                onClick={() => {
                  setOverlapNotice(null);
                  navigate("/operations/alerts");
                }}
              >
                Revisar alerta
              </button>
            </div>
            <button
              type="button"
              className="group-overlap-toast__close"
              onClick={() => setOverlapNotice(null)}
              aria-label="Cerrar aviso"
            >
              <X />
            </button>
          </aside>
        )}
        {releaseNotice && (
          <aside
            className="release-update-toast"
            role="alert"
            aria-labelledby="release-update-title"
          >
            <span className="release-update-toast__icon">
              <RefreshCw />
            </span>
            <div>
              <strong id="release-update-title">
                Hay una nueva versión de RestLab
              </strong>
              <p>{releaseNotice.message}</p>
              <small>
                La sesión seguirá abierta y volverás a esta misma pantalla.
              </small>
            </div>
            <div className="release-update-toast__actions">
              <button
                type="button"
                className="button button--outline"
                onClick={() => {
                  sessionStorage.setItem(
                    "restlab-dismissed-release-notice",
                    `${releaseNotice.currentRelease}:${releaseNotice.publishedAt ?? "unpublished"}`,
                  );
                  setReleaseNotice(null);
                }}
              >
                Más tarde
              </button>
              <button
                type="button"
                className="button button--primary"
                onClick={() => window.location.reload()}
              >
                <RefreshCw /> Recargar ahora
              </button>
            </div>
          </aside>
        )}
      </div>

      <nav className="bottom-nav" aria-label="Navegación móvil">
        {isStandby ? (
          <>
            {canManageBilling && (
              <NavLink to={billingTarget}>
                <FileText />
                <span>Facturación</span>
              </NavLink>
            )}
            <NavLink to={`/locations/${effectiveLocationId}/settings`}>
              <Settings2 />
              <span>Configuración</span>
            </NavLink>
          </>
        ) : (
          <>
            <NavLink to={`/locations/${effectiveLocationId}/dashboard`}>
              <LayoutDashboard />
              <span>Inicio</span>
            </NavLink>
            <NavLink to={`/locations/${effectiveLocationId}/book`}>
              <Utensils />
              <span>Reservas</span>
            </NavLink>
            <NavLink to={`/locations/${effectiveLocationId}/calendar`}>
              <CalendarDays />
              <span>Calendario</span>
            </NavLink>
            {hasGroupAccess && (
              <NavLink to="/group">
                <BarChart3 />
                <span>Grupo</span>
              </NavLink>
            )}
          </>
        )}
        <button onClick={() => setMobileMenu(true)}>
          <Menu />
          <span>Más</span>
        </button>
      </nav>
    </div>
  );
}
